<template>
	<div>
		<v-form
			ref="form"
			v-model="valid"
			lazy-validation
			:class="['poll-form px-3 mb-3', { 'poll-form-event': !postId }]"
			@submit.prevent="validateAndSubmit"
		>
			<div class="poll-form__title">{{ poll.poll_question }}</div>

			<div
				v-for="answer in poll.pollsAnswers"
				v-if="postId"
				:key="answer.poll_answer_id"
				:class="{ disabled: isPollClosed }"
				@click="handleAnswerClick(answer.poll_answer_id)"
			>
				<div
					class="poll-result__answer-scale"
					:style="{ cursor: isPollVoted ? 'default' : 'pointer' }"
				>
					<div class="poll-result__answer-scale-bar" :style="getScaleBarStyle(answer)" />
					<div class="poll-result__answer-scale__answer">
						{{ answer.is_custom ? 'Custom answer' : answer.poll_answer }}
					</div>
					<div
						:class="[
							'poll-result__answer-scale__percentage',
							{
								'poll-result__answer-scale__percentage--bold':
									isMaxPercentage(answer),
							},
						]"
					>
						{{ Math.round(calcPercentage(answer)) }} %
					</div>
				</div>
			</div>
		</v-form>

		<div class="button-container">
			<span
				class="poll-result__answers-count"
				:style="votesCountStyle"
				@click="goToPollUsers"
			>
				{{ answersCount }} Votes
			</span>

			<button
				v-if="!isPollClosed && this.poll.vote"
				type="button"
				class="poll-result__edit-vote"
				@click="handleRevote"
			>
				Edit Vote
			</button>

			<span class="poll-result__answers-count">
				{{ pollStatusText }}
			</span>
		</div>

		<div v-if="isMyPost && !isPollClosed" class="button-close-poll mt-3">
			<button
				type="button"
				class="poll-form__submit-button elevation-0"
				@click="stopPollStatus"
			>
				Close Poll
			</button>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import Dwh from '@/mixins/Dwh'

const AT_LEAST_TYPE = 0
const AT_MOST_TYPE = 1
const EXACTLY_TYPE = 2
const CLOSED_POLL_STATUS = 7

export default {
	mixins: [Form, Dwh],

	props: {
		poll: {
			type: Object,
			default: () => ({}),
		},
		postId: {
			type: Number,
			default: null,
		},
		post: {
			type: Object,
			default: () => ({}),
		},
		answers: {
			type: Array,
			default: () => [],
		},
		isEventVoteDirty: {
			type: Boolean,
			default: false,
		},
		pollsLoading: {
			type: Boolean,
			default: false,
		},
		typeValue: {
			type: String,
			default: '',
		},
	},

	data() {
		return {
			fields: {
				answer: [],
				customAnswer: '',
			},
			isDirty: false,
		}
	},

	computed: {
		...mapGetters('settings', ['styles']),
		...mapGetters('post', ['isPollVoting']),

		isPollClosed() {
			return this.post.poll.poll_status === CLOSED_POLL_STATUS
		},

		pollStatusText() {
			return this.isPollClosed ? 'Closed' : 'In Progress'
		},

		isMyPost() {
			return this.post.user.user_id === this.profile.user_id
		},

		isPollVoted() {
			return this.poll.vote
		},

		isSubmitEnabled() {
			return Array.isArray(this.fields.answer)
				? this.fields.answer.length
				: this.fields.answer
		},

		answersCount() {
			return this.poll.pollsAnswers.reduce((acc, item) => acc + item.poll_answer_count, 0)
		},

		customAnswerIsValid() {
			if (Array.isArray(this.fields.answer)) {
				return this.fields.answer.some((item) => item === 'custom')
					? !!this.fields.customAnswer
					: true
			}

			return this.fields.answer === 'custom' ? !!this.fields.customAnswer : true
		},

		isCheckboxesValid() {
			if (!Array.isArray(this.fields.answer)) {
				return !!this.fields.answer || 'Answer required'
			}

			switch (this.poll.require_answer_option) {
				case AT_LEAST_TYPE:
					return (
						this.poll.num_of_answers <= this.fields.answer.length ||
						`At least ${this.poll.num_of_answers} variants required`
					)

				case AT_MOST_TYPE:
					return (
						this.poll.num_of_answers >= this.fields.answer.length ||
						`At most ${this.poll.num_of_answers} variants required`
					)

				case EXACTLY_TYPE:
					return (
						this.poll.num_of_answers === this.fields.answer.length ||
						`Exactly ${this.poll.num_of_answers} variants required`
					)

				default:
					return null
			}
		},

		maxPercentage() {
			return Math.max(
				...this.poll.pollsAnswers.map((answer) =>
					parseFloat(this.calcPercentage(answer).toFixed(1))
				)
			)
		},

		votesCountStyle() {
			return {
				color: this.isMyPost ? 'var(--text_button_tint)' : '#4A4A4A',
				cursor: this.isMyPost ? 'pointer' : 'default',
			}
		},
	},

	watch: {
		'fields.answer': {
			handler(newValue) {
				if (!this.postId) {
					const answer = {
						poll_id: this.poll.poll_id,
						answer: newValue,
						error:
							typeof this.isCheckboxesValid === 'string'
								? this.isCheckboxesValid
								: this.customAnswerIsValid
								? null
								: 'Custom answer cannot be empty',
					}

					if (newValue === 'custom') {
						answer.customAnswer = this.fields.customAnswer
					}

					this.$emit('answerChange', answer)
				}
			},
		},

		'fields.customAnswer': {
			handler(newValue) {
				if (!this.postId) {
					this.$emit('answerChange', {
						poll_id: this.poll.poll_id,
						answer: 'custom',
						customAnswer: newValue,
						error: newValue.length ? null : 'Custom answer cannot be empty',
					})
				}
			},
		},
	},

	methods: {
		...mapActions('post', ['votePoll', 'pollStatus']),

		getScaleBarStyle(answer) {
			const percentage = this.calcPercentage(answer)

			return {
				width: `${percentage}%`,
				backgroundColor:
					answer.selected === 1 ? 'var(--vote_bar_tint)' : 'rgba(226, 226, 226, 0.3)',
				borderRadius: Math.round(percentage) === 100 ? '8px' : '8px 0 0 8px',
			}
		},

		handleAnswerClick(answerId) {
			if (this.isPollClosed || this.isPollVoted) return
			this.selectAnswer(answerId)
		},

		stopPollStatus() {
			if (this.isPollClosed) return

			this.pollStatus({
				thread_id: this.post.thread_id,
			})
				.then(() => {
					this.$set(this.post.poll, 'poll_status', CLOSED_POLL_STATUS)
				})
				.catch((error) => {
					console.error(error)
				})
		},

		goToPollUsers() {
			if (!this.isMyPost || !this.post?.thread_id) return

			this.$router.push({
				name: 'polls-users',
				query: {
					thread_id: this.post.thread_id,
				},
			})
		},

		handleRevote() {
			if (!this.poll.vote) return
			this.poll.vote = false
		},

		selectAnswer(answerId) {
			if (this.pollsLoading) return

			this.fields.answer = answerId
			this.handleSubmit()
		},

		async submitForm() {
			if (this.isPollVoting) return

			const payload = {
				thread_id: this.post.thread_id,
			}

			if (!this.post.poll.pollsAnswers.some((answer) => answer.selected === 1)) {
				payload.vote_from = this.typeValue
			}

			if (this.fields.answer === 'custom') {
				payload.custom_answer = this.fields.customAnswer
			} else {
				payload.poll_answer_id = this.fields.answer
			}

			const isThreads = this.$route.name === 'threads'
			await this.votePoll({ poll: payload, postId: this.postId, isThreads })
		},

		calcPercentage(answer) {
			const percentage = (answer.poll_answer_count / this.answersCount) * 100 || 0
			return parseFloat(percentage.toFixed(1))
		},

		isMaxPercentage(answer) {
			return Math.abs(this.calcPercentage(answer) - this.maxPercentage) < 0.1
		},

		handleSubmit() {
			if (!this.isSubmitEnabled) return

			this.isDirty = true

			if (this.customAnswerIsValid && typeof this.isCheckboxesValid !== 'string') {
				this.validateAndSubmit()

				if (['threads', 'posts-by-id'].includes(this.$route.name)) {
					this.sendTrigger({
						category: 'Content',
						action: 'click_vote_poll_button_news_feed',
						module: 'Polls',
						data: this.poll.poll_id,
						content_chapter_id:
							this.post && this.post.chapter_id ? [this.post.chapter_id] : null,
					})
				}
			}
		},
	},

	mounted() {
		const option = this.poll.require_answer_option

		if (
			this.poll.num_of_answers === 1 &&
			(option === AT_MOST_TYPE || option === EXACTLY_TYPE)
		) {
			this.fields.answer = null
		} else {
			this.fields.answer = []
		}
	},
}
</script>

<style lang="scss">
.button-container {
	display: flex;
	justify-content: space-between;
	padding: 0 12px;
}

.button-close-poll {
	padding: 0 12px;
}

.poll-form {
	padding-top: 12px !important;
	font-family: Inter, sans-serif;

	&-event {
		margin-bottom: 12px;
		padding: 16px;
		border: 1px solid #e2e2e2;
		border-radius: 10px;
		background-color: #f3f3f6;
	}

	&__title {
		margin-bottom: 10px !important;
		color: var(--accent-color);
		font-family: Inter, sans-serif;
		font-size: 18px;
		font-weight: 700;
		line-height: 100%;
		letter-spacing: 0;
	}

	&__answer-new {
		display: flex;
		align-items: center;
		min-height: 44px;
		padding: 12px;
		border-radius: 8px;
		background-color: #f3f3f6;
		font-family: Inter, sans-serif;
		font-size: 16px;
		font-weight: 500;
		line-height: 100%;
		letter-spacing: 0;

		&__title {
			color: #002855;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
		}
	}

	&__answer {
		i {
			font-size: 20px !important;
			color: #e2e2e2 !important;
		}

		label {
			color: var(--accent-color) !important;
			font-size: 14px;
			line-height: 17px;
			word-break: break-word;
		}
	}

	&__custom-answer {
		border-bottom: 1px solid #e2e2e2;

		input {
			padding: 0 !important;
		}

		.v-input__slot::after {
			border-color: var(--accent-color) !important;
		}
	}

	.v-input--is-label-active {
		i {
			color: var(--accent-color) !important;
		}
	}

	&__submit-button {
		width: 100%;
		height: 50px;
		border: 1.5px solid var(--button-secondary-background-color);
		border-radius: 8px;
		background-color: #ffffff;
		color: var(--button-secondary-background-color);
		font-family: Inter, sans-serif;
		font-size: 18px;
		font-weight: 700;
		line-height: 21.78px;
		text-transform: capitalize;
	}

	&__error {
		position: absolute;
		top: -8px;
		left: 50%;
		margin: 0;
		transform: translateX(-50%);
		color: red;
		font-size: 12px;
	}
}

.poll-result {
	padding-top: 12px !important;
	font-family: Inter, sans-serif;

	&__answer-scale {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 44px;
		margin-bottom: 8px;
		padding: 12px;
		gap: 8px;
		border-radius: 8px;
		background-color: #f3f3f6 !important;
		line-height: 100%;
		letter-spacing: 0;

		&__answer,
		&__percentage {
			position: relative;
			z-index: 2;
		}

		&__answer {
			color: #4a4a4a;
			font-family: Inter, sans-serif;
			font-size: 16px;
			font-weight: 500;
			line-height: 100%;
			letter-spacing: 0;
		}

		&__percentage {
			white-space: nowrap;
			font-family: Inter, sans-serif;
			font-size: 16px;
			font-weight: 400;
			line-height: 100%;
			letter-spacing: 0;
		}

		&__percentage--bold {
			font-weight: 700 !important;
		}
	}

	&__answer-scale-bar {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		height: 100%;
		transition: width 0.3s ease;
	}

	&__edit-vote {
		color: var(--text_button_tint);
		font-family: Inter, sans-serif;
		font-size: 18px;
		font-weight: 700;
		line-height: 21.78px;
	}

	&__answers-count {
		color: #4a4a4a;
		font-family: Inter, sans-serif;
		font-size: 18px;
		font-weight: 700;
		line-height: 21.78px;
	}

	&-event {
		margin-bottom: 10px;
		padding: 16px;
		border: 1px solid #e2e2e2 !important;
		border-radius: 10px !important;
		background-color: #f3f3f6 !important;

		.v-progress-linear__buffer {
			background-color: #fff !important;
		}
	}
}

.disabled {
	pointer-events: none;
	cursor: not-allowed;
	opacity: 0.6;
}
</style>
